import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { categoryLabel, formatDate, formatIDR, LAYANAN_CARE_TYPES, optionLabel } from "../lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import {
  Wallet,
  HandCoins,
  TrendingUp,
  TrendingDown,
  Boxes,
  Loader2,
  Ambulance,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const PIE_COLORS = ["#065f46", "#eab308", "#0284c7"];

const tooltipStyle = {
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  fontSize: "12px",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (user && !["admin", "pengurus", "operator"].includes(user.role)) {
    return <Navigate to="/layanan" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-800" />
      </div>
    );
  }

  const d = data || {};
  const stats = [
    { label: "Saldo Dana Infaq/Sedekah", value: d.saldo_infaq, icon: Wallet, color: "text-emerald-800 bg-emerald-100", testid: "stat-saldo-infaq" },
    { label: "Saldo Dana Zakat", value: d.saldo_zakat, icon: HandCoins, color: "text-yellow-700 bg-yellow-100", testid: "stat-saldo-zakat" },
    { label: "Penerimaan Bulan Ini", value: d.month_in, icon: TrendingUp, color: "text-emerald-800 bg-emerald-100", testid: "stat-month-in" },
    { label: "Pentasyarufan Bulan Ini", value: d.month_out, icon: TrendingDown, color: "text-red-600 bg-red-100", testid: "stat-month-out" },
    { label: "Nilai Buku Aset", value: d.asset_summary?.total_book, icon: Boxes, color: "text-sky-700 bg-sky-100", testid: "stat-asset-value" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-800">Dasbor</h1>
        <p className="text-sm text-slate-500 mt-1">Ikhtisar keuangan LAZISNU Dawe berdasarkan PSAK 109</p>
        {user?.role === "operator" && (
          <p className="mt-2 inline-block rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-800" data-testid="dashboard-scope-note">
            Menampilkan ikhtisar data yang Anda input
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <Card key={s.testid} className="transition-all duration-200 hover:-translate-y-[2px] hover:shadow-sm" data-testid={s.testid}>
            <CardContent className="p-5">
              <div className={`mb-3 inline-flex h-9 w-9 items-center justify-center rounded-lg ${s.color}`}>
                <s.icon className="h-4 w-4" strokeWidth={1.8} />
              </div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">{s.label}</p>
              <p className="mt-1 text-xl font-bold text-slate-900 font-heading truncate" data-testid={`${s.testid}-value`}>
                {formatIDR(s.value)}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="chart-arus-kas">
          <CardHeader>
            <CardTitle className="text-lg">Arus Kas 6 Bulan Terakhir</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={d.monthly || []} margin={{ left: 8, right: 8 }}>
                <defs>
                  <linearGradient id="gMasuk" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#065f46" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#065f46" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gKeluar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${(v / 1000000).toFixed(0)}jt`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatIDR(v)} />
                <Legend />
                <Area type="monotone" dataKey="masuk" name="Dana Masuk" stroke="#065f46" fill="url(#gMasuk)" strokeWidth={2} />
                <Area type="monotone" dataKey="keluar" name="Dana Keluar" stroke="#ef4444" fill="url(#gKeluar)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-aset">
          <CardHeader>
            <CardTitle className="text-lg">Komposisi Aset (Nilai Buku)</CardTitle>
          </CardHeader>
          <CardContent>
            {(d.asset_summary?.composition || []).length === 0 ? (
              <p className="py-16 text-center text-sm text-slate-400">Belum ada aset tercatat</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={d.asset_summary.composition}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={3}
                  >
                    {d.asset_summary.composition.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatIDR(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="chart-dana">
          <CardHeader>
            <CardTitle className="text-lg">Penerimaan vs Pentasyarufan per Dana</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.fund_chart || []} margin={{ left: 8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${(v / 1000000).toFixed(0)}jt`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatIDR(v)} />
                <Legend />
                <Bar dataKey="masuk" name="Penerimaan" fill="#065f46" radius={[4, 4, 0, 0]} />
                <Bar dataKey="keluar" name="Pentasyarufan" fill="#eab308" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="recent-transactions">
          <CardHeader>
            <CardTitle className="text-lg">Transaksi Terbaru</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {(d.recent || []).length === 0 ? (
              <p className="px-6 pb-8 text-center text-sm text-slate-400">Belum ada transaksi</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Uraian</TableHead>
                    <TableHead className="text-right">Jumlah</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.recent.map((tx) => (
                    <TableRow key={tx.tx_id}>
                      <TableCell>
                        <p className="text-sm font-medium text-slate-800">{categoryLabel(tx.category)}</p>
                        <p className="text-xs text-slate-400">{formatDate(tx.tx_date)} · {tx.donor || tx.recipient || tx.created_by_name || "-"}</p>
                      </TableCell>
                      <TableCell className={`text-right text-sm font-semibold whitespace-nowrap ${tx.direction === "in" ? "text-emerald-700" : "text-red-600"}`}>
                        {tx.direction === "in" ? "+" : "-"}{formatIDR(tx.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      {d.layanan && (
        <Card data-testid="dashboard-layanan">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Ambulance className="h-5 w-5 text-emerald-800" />
              Ikhtisar Permohonan Layanan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-8 mb-4">
              <div>
                <p className="text-3xl font-bold font-heading text-slate-900" data-testid="layanan-total">{d.layanan.total}</p>
                <p className="text-xs text-slate-500">Total permohonan</p>
              </div>
              <div>
                <p className="text-3xl font-bold font-heading text-emerald-800" data-testid="layanan-bulan-ini">{d.layanan.bulan_ini}</p>
                <p className="text-xs text-slate-500">Bulan ini</p>
              </div>
            </div>
            {(d.layanan.mendatang || []).length === 0 ? (
              <p className="text-sm text-slate-400">Tidak ada jadwal layanan mendatang</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {d.layanan.mendatang.map((r) => (
                  <div key={r.request_id} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    <p className="font-medium text-slate-800">{r.patient_name}</p>
                    <p className="text-xs text-slate-400">
                      {r.day}, {formatDate(r.date)}{r.time ? ` · ${r.time}` : ""}{r.desa ? ` · Desa ${r.desa}` : ""}
                    </p>
                    <span className="mt-1 inline-block rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800">
                      {optionLabel(LAYANAN_CARE_TYPES, r.care_type)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
